import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Activity, KeyRound, Loader2 } from "lucide-react";

import { api, auth } from "@/api/client";
import { ClientLogo } from "@/components/ClientLogo";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: { pathname: string; search?: string }; expired?: boolean } | null;
  const from = state?.from ? state.from.pathname + (state.from.search ?? "") : "/";
  const expired = Boolean(state?.expired);

  const [mode, setMode] = useState<"password" | "apikey">("password");
  const [email, setEmail] = useState("admin@kinetix.local");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "password") {
        await api.login(email, password);
      } else {
        // Store the key, then make one authenticated call to prove it works
        // rather than letting the user discover it is wrong on the next page.
        auth.setApiKey(apiKey.trim());
        await api.me();
      }
      navigate(from, { replace: true });
    } catch (err) {
      auth.clear();
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full place-items-center bg-ink-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <ClientLogo height={56} />
          <div className="flex items-center gap-2 text-sm text-ink-400">
            <Activity className="h-4 w-4 text-brand-400" strokeWidth={2.5} />
            <span>
              <span className="font-semibold text-ink-200">Kinetix</span> · Structural Health Monitoring
            </span>
          </div>
        </div>

        <form onSubmit={submit} className="card space-y-4 p-6">
          <div className="flex rounded-lg border border-ink-700 p-0.5">
            {(["password", "apikey"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setError(null);
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  mode === m ? "bg-brand-600 text-white" : "text-ink-300 hover:bg-ink-800"
                }`}
              >
                {m === "password" ? "Email & password" : "API key"}
              </button>
            ))}
          </div>

          {mode === "password" ? (
            <>
              <Field label="Email">
                <input
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </Field>
              <Field label="Password">
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
            </>
          ) : (
            <Field label="API key">
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
                <input
                  type="password"
                  className="input pl-9 font-mono text-xs"
                  placeholder="kxk_…"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                />
              </div>
            </Field>
          )}

          {expired && !error && (
            <div className="rounded-lg border border-ink-700 bg-ink-800/60 px-3 py-2 text-xs text-ink-300">
              Your session has ended. Sign in again to carry on where you were.
            </div>
          )}
          {error && (
            <div
              className="rounded-lg border px-3 py-2 text-xs"
              style={{
                borderColor: "#d03b3b55",
                backgroundColor: "#d03b3b1a",
                color: "#e88f8f",
              }}
              role="alert"
            >
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </button>
        </form>

        <p className="mt-4 text-center text-[11px] text-ink-500">
          Seeded demo credentials are printed by{" "}
          <code className="rounded bg-ink-800 px-1 py-0.5 font-mono">python -m scripts.seed</code>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="label">{label}</span>
      {children}
    </label>
  );
}
